import { Coins } from 'lucide-react';

import { formatCredits, formatCreditsWithDollar } from '@/lib/format-credits';
import { cn } from '@/lib/utils';

interface InteractionCostLabelProps {
  /**
   * Credits this single interaction actually charged the user, after the
   * gateway finalized it (1 credit = 1 US cent). `undefined`/`null` means the
   * cost isn't known yet (e.g. still streaming) → nothing renders.
   */
  costCredits: number | null | undefined;
  className?: string;
}

/**
 * Renders what ONE interaction (a chat response, a generated image) actually
 * cost the user, as a compact muted label with the dollar equivalent in a hover
 * tooltip. This is the CHARGED amount from the credit ledger — distinct from
 * {@link ModelCostLabel}, which shows a model's per-unit RATE card.
 *
 * Returns `null` when the cost is unknown, so callers can drop it in
 * unconditionally and it simply appears once the interaction finishes.
 */
export function InteractionCostLabel({ costCredits, className }: InteractionCostLabelProps) {
  if (costCredits === null || costCredits === undefined || !Number.isFinite(costCredits)) {
    return null;
  }

  const isFree = costCredits <= 0;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-[10px] leading-tight text-muted-foreground',
        className,
      )}
      title={isFree ? 'No credits charged' : `Cost: ${formatCreditsWithDollar(costCredits)}`}
    >
      <Coins className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span>{isFree ? 'Free' : `${formatCredits(costCredits)} credits`}</span>
    </span>
  );
}
